import React, { useState } from 'react';

import { FormattedMessage } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';

import FavouriteToggle from '../../components/FavouriteToggle/FavouriteToggle';

import css from './ListingActions.module.css';

/**
 * Navigation and sharing actions shown above the photos on every public listing layout.
 * The public URL deliberately excludes any query, hash, or private draft route.
 */
const ListingActions = ({
  routes,
  marketplaceRootURL,
  listingId,
  slug,
  title,
  vehicleSummary,
  photoUrl,
  showSave,
  showShare = true,
}) => {
  const [copyStatus, setCopyStatus] = useState(null);
  const [manualText, setManualText] = useState(null);
  const listingPath = createResourceLocatorString(
    'ListingPage',
    routes,
    { id: listingId, slug },
    {}
  );
  const url = `${(marketplaceRootURL || '').replace(/\/$/, '')}${listingPath}`;
  const vehicleMessage = `${vehicleSummary || title}\n${url}`;

  const copyText = async value => {
    setCopyStatus(null);
    setManualText(null);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        setCopyStatus('copied');
        return;
      } catch (error) {
        // Clipboard permission can be denied: try the older copy method.
      }
    }

    if (typeof document !== 'undefined') {
      const field = document.createElement('textarea');
      field.value = value;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      document.body.appendChild(field);
      field.select();
      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        // Offer a selectable URL if the browser blocks automatic copying.
      } finally {
        document.body.removeChild(field);
      }
      if (copied) {
        setCopyStatus('copied');
        return;
      }
    }
    setManualText(value);
    setCopyStatus('manual');
  };

  const handleShare = async () => {
    setCopyStatus(null);
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, text: vehicleSummary || title, url });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await copyText(vehicleMessage);
  };

  const handleSharePhoto = async () => {
    // File sharing is best-effort. A photo CDN may not permit CORS, and not
    // all mobile browsers support files in the native sharing sheet.
    if (
      photoUrl &&
      typeof fetch === 'function' &&
      typeof File !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function' &&
      typeof navigator.canShare === 'function'
    ) {
      try {
        const response = await fetch(photoUrl, { mode: 'cors' });
        if (!response.ok) throw new Error('Vehicle photo unavailable');
        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) throw new Error('Unexpected photo format');
        const ext = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : 'jpg';
        const image = new File([blob], `vehicle-${listingId}.${ext}`, { type: blob.type });
        if (navigator.canShare({ files: [image] })) {
          await navigator.share({ files: [image], title, text: vehicleSummary || title, url });
          return;
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        // Fallback sends the accurate vehicle details and public listing URL.
      }
    }
    await handleShare();
  };

  const copyLink = () => copyText(url);
  const copyVehicle = () => copyText(vehicleMessage);

  return (
    <div className={css.wrapper}>
      <nav className={css.actions} aria-label="Vehicle actions">
        {showShare ? (
          <>
            <button type="button" className={css.primaryAction} onClick={handleShare}>
              <span aria-hidden="true">↗</span>{' '}
              <FormattedMessage id="ListingActions.share" defaultMessage="Share" />
            </button>
            {photoUrl ? (
              <button type="button" className={css.action} onClick={handleSharePhoto}>
                <FormattedMessage id="ListingActions.sharePhoto" defaultMessage="Share photo" />
              </button>
            ) : null}
            <button type="button" className={css.action} onClick={copyVehicle}>
              <FormattedMessage
                id="ListingActions.copyVehicle"
                defaultMessage="Copy vehicle details"
              />
            </button>
            <button type="button" className={css.action} onClick={copyLink}>
              <FormattedMessage id="ListingActions.copyLink" defaultMessage="Copy link" />
            </button>
          </>
        ) : null}
        {showSave ? (
          <FavouriteToggle listingId={listingId} compact className={css.favourite} />
        ) : null}
      </nav>
      {copyStatus === 'copied' ? (
        <p className={css.status} role="status">
          <FormattedMessage id="ListingActions.linkCopied" defaultMessage="Copied to clipboard" />
        </p>
      ) : null}
      {copyStatus === 'manual' ? (
        <div className={css.copyFallback}>
          <label htmlFor="listing-share-link">
            <FormattedMessage
              id="ListingActions.copyManually"
              defaultMessage="Select and copy these vehicle details:"
            />
          </label>
          <textarea
            id="listing-share-link"
            readOnly
            value={manualText || vehicleMessage}
            rows={6}
            onFocus={event => event.target.select()}
            onClick={event => event.target.select()}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ListingActions;
